import { useMemo, useState } from "react";
import { router, useForm } from "@inertiajs/react";
import Layout from "@/layouts/layout";
import PageHeading from "@/components/layout/PageHeading";
import Panel from "@/components/ui/Panel";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import { Field, Input } from "@/components/ui/Field";

type GrantRate = {
  id: number;
  country: string;
  price_index: number | null;
  per_signup_cents: number;
  per_attendee_half_show_cents: number;
  events_count: number;
};

type Unmatched = { country: string; events_count: number };

function money(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

function dollars(cents: number) {
  return (cents / 100).toFixed(2);
}

function centsFrom(value: string) {
  return Math.round(parseFloat(value || "0") * 100);
}

// One country's rate. Kept as its own component so each row owns the value
// being typed into it -- 185 rows sharing one form object is a lot of
// re-rendering for every keystroke.
function RateRow({ rate }: { rate: GrantRate }) {
  const [value, setValue] = useState(dollars(rate.per_signup_cents));
  const dirty = centsFrom(value) !== rate.per_signup_cents;

  function save() {
    router.patch(
      `/admin/grant-rates/${rate.id}`,
      { per_signup_cents: centsFrom(value) },
      { preserveScroll: true },
    );
  }

  return (
    <div className="border-line/20 flex flex-wrap items-center gap-3 border-b py-2.5 last:border-b-0">
      <div className="min-w-0 flex-1 basis-48">
        <p className="text-body text-ink truncate font-bold">{rate.country}</p>
        <p className="text-caption text-ink-soft">
          Price index {rate.price_index ?? "-"}
          {rate.events_count > 0 &&
            ` · ${rate.events_count} ${rate.events_count === 1 ? "event" : "events"}`}
        </p>
      </div>

      <div className="flex items-center gap-1">
        <span className="text-caption text-ink-soft">$</span>
        <Input
          type="number"
          step="0.01"
          min={0}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-24"
          aria-label={`Per signup rate for ${rate.country}`}
        />
      </div>

      {/* Twice the signup rate: what each person in the room is worth when the
          usual half of the RSVPs turn up. */}
      <span className="text-caption text-ink-muted w-32 shrink-0">
        {money(centsFrom(value) * 2)} at 50%
      </span>

      <div className="flex shrink-0 gap-2">
        <Button size="sm" onClick={save} disabled={!dirty}>
          Save
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            if (
              confirm(
                `Remove ${rate.country}? It falls back to the platform rate.`,
              )
            )
              router.delete(`/admin/grant-rates/${rate.id}`, {
                preserveScroll: true,
              });
          }}
        >
          Remove
        </Button>
      </div>
    </div>
  );
}

export default function AdminGrantRates({
  rates,
  unmatched,
  fallback_cents,
}: {
  rates: GrantRate[];
  unmatched: Unmatched[];
  fallback_cents: number;
}) {
  const [query, setQuery] = useState("");
  const { data, setData, post, processing, errors, reset, transform } = useForm(
    {
      country: "",
      per_signup_dollars: "",
    },
  );

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? rates.filter((r) => r.country.toLowerCase().includes(q)) : rates;
  }, [rates, query]);

  return (
    <Layout>
      <div className="flex flex-col gap-8">
        <PageHeading
          eyebrow="Admin"
          title="Grant rates"
          subtitle={`What one verified RSVP is worth, by country. Anywhere not listed pays the ${money(fallback_cents)} fallback from settings.`}
        />

        {/* Countries organizers have typed that nothing answers for. Every one
            of these is being paid the fallback right now, so they're the rows
            worth adding first. */}
        {unmatched.length > 0 && (
          <Panel
            title="no-rate.csv"
            actions={<Badge tone="warning">Paying the fallback</Badge>}
          >
            <div className="flex flex-col gap-1">
              <p className="text-caption text-ink-muted pb-1">
                Events exist in these countries, but the card has no rate for
                them. Add one below and they start paying it immediately.
              </p>
              {unmatched.map((row) => (
                <p key={row.country} className="text-body text-ink font-bold">
                  {row.country}{" "}
                  <span className="text-caption text-ink-soft font-normal">
                    · {row.events_count}{" "}
                    {row.events_count === 1 ? "event" : "events"}
                  </span>
                </p>
              ))}
            </div>
          </Panel>
        )}

        <Panel title="add-country.form">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              // The field is in dollars because that's how the rate card reads;
              // the column is cents.
              transform((d) => ({
                country: d.country,
                per_signup_cents: centsFrom(d.per_signup_dollars),
              }));
              post("/admin/grant-rates", {
                preserveScroll: true,
                onSuccess: () => reset(),
              });
            }}
            className="flex flex-wrap items-end gap-4"
          >
            <Field label="Country" error={errors.country}>
              <Input
                value={data.country}
                onChange={(e) => setData("country", e.target.value)}
                placeholder="Kiribati"
                required
              />
            </Field>

            <Field
              label="Per signup"
              hint="In dollars. The 50% figure is twice this."
              error={errors.per_signup_cents}
            >
              <Input
                type="number"
                step="0.01"
                min={0}
                value={data.per_signup_dollars}
                onChange={(e) => setData("per_signup_dollars", e.target.value)}
                placeholder="3.50"
                required
              />
            </Field>

            <Button type="submit" disabled={processing}>
              Add
            </Button>
          </form>
        </Panel>

        <Panel title={`grant-rates.csv - ${rates.length}`}>
          <div className="flex flex-col">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by country"
              className="mb-3 max-w-xs"
              aria-label="Filter by country"
            />

            {shown.length === 0 ? (
              <p className="text-caption text-ink-muted py-3">
                Nothing matches “{query}”.
              </p>
            ) : (
              shown.map((rate) => <RateRow key={rate.id} rate={rate} />)
            )}
          </div>
        </Panel>
      </div>
    </Layout>
  );
}
